/**
 * Checks if a numeric value is positive (greater than zero).
 *
 * @example
 * ```typescript
 * isPositive(5) // returns true
 * isPositive("10.5") // returns true
 * isPositive(0) // returns false
 * isPositive(-3) // returns false
 * isPositive("0") // returns false
 * isPositive("-7.2") // returns false
 * ```
 */
export const isPositive = (value: string | number): boolean => Math.sign(Number(value)) === 1;

/**
 * Formats a percentage value by adding a '+' prefix for positive values and a '%' suffix for all values.
 *
 * @example
 * ```typescript
 * formatPercentage(5) // returns "+5%"
 * formatPercentage(-3) // returns "-3%"
 * formatPercentage("2.5") // returns "+2.5%"
 * ```
 */
export const formatPercentage = (value: string | number) => (isPositive(value) ? `+${value}%` : `${value}%`);

/**
 * A common sense function to format a number as a price in GBP.
  * @example
  *
  * ```typescript
  * formatPrice(1000) // returns "£1,000"
  * formatPrice(1000.5) // returns "£1,000.50"
  * ```
 */
export const formatPrice = (
  value: string | number,
  options?: Intl.NumberFormatOptions,
): string => {
  const number = Number(value);
  const fractionDigits = Number.isInteger(number) ? 0 : 2;

  return new Intl.NumberFormat('en-GB', {
    currency: 'GBP',
    maximumFractionDigits: fractionDigits,
    minimumFractionDigits: fractionDigits,
    style: 'currency',
    ...options,
  }).format(number);
};

/**
 * A tailored function to format a number as a price in GBP.
 * It's primary used in context where showing decimal points is required,
 * even if they are '.00'.
 *
 * @example
 * ```typescript
 * formatPriceTransactional(1000) // returns "£1,000.00"
 * formatPriceTransactional(1000.1) // returns "£1,000.10"
 * formatPriceTransactional(1000.12) // returns "£1,000.12"
 * ```
 */
export const formatPriceTransactional: typeof formatPrice = (value, opts) => formatPrice(value, {
  ...opts,
  maximumFractionDigits: 2,
  minimumFractionDigits: 2,
});

/**
 * Formats a number as an abbreviated GBP price in thousands.
 *
 * @example
 * ```typescript
 * abbreviatePrice(27000) // returns "£27k"
 * abbreviatePrice(27337) // returns "£27.3k"
 * abbreviatePrice(500) // returns "£500"
 * ```
 */
export const abbreviatePrice = (value: string | number): string => {
  const number = Number(value);

  if (!Number.isFinite(number) || Math.abs(number) < 1000) {
    return formatPrice(number);
  }

  const thousands = number / 1000;
  const rounded = Math.round(thousands * 10) / 10;

  return `£${Number.isInteger(rounded) ? rounded : rounded.toFixed(1)}k`;
};
